/**
 * @version 1.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://kelnik.gitbooks.io/kelnik-documentation/front-end/components/slider.html
 */

/**
 * DEPENDENCIES
 */
import arrowTemplate from './templates/arrows-tmpl.twig';
import captionTemplate from './templates/caption-tmpl.twig';
import counterTemplate from './templates/counter-tmpl.twig';
import dotsTemplate from './templates/dots-tmpl.twig';
import Hammer from 'hammerjs';
import Observer from 'common/scripts/observer';
import slideTemplate from './templates/slides-tmpl.twig';
import thumbnailsTemplate from './templates/thumbnails-tmpl.twig';
import Utils from 'common/scripts/utils';

const observer = new Observer();

class Slider {
    constructor() {
        /**
         * Нода слайдера
         * @type {node}
         */
        this.slider = {};

        this.slidesWrap = ``;

        this.slideVisibleDefaul = 1;

        /**
         * Номер активного слайда - индекс. При загрузщке страницы это первый слайд.
         * @type {number}
         */
        this.activeSlide = 0;

        /**
         * Класс активного элемента. Задаётся активной точке, пказанному в данный момент слайду и тумбочке
         */
        this.isActive = 'is-active';

        /**
         * Класс неактивного элемента. Задаётся стрелкам, в случае если на нее нельзя нажать (последний слайд)
         */
        this.isDisable = 'is-disabled';

        /**
         * Показываем или нет стрелки
         * @type {boolean}
         */
        this.arrow = null;
        this.arrowLeft = null;
        this.arrowRight = null;

        /**
         * Показываем или нет точки
         * @type {boolean}
         */
        this.dots = null;

        /**
         * Показываем или нет счётчик слайдов
         * @type {boolean}
         */
        this.counter = null;

        /**
         * количество тумбочек к показу под галереей в цыфарках. Возможно значение auto - ширина по размеру контента
         * @type {number|string}
         */
        this.thumbnailsVisible = null;

        this.thumbnailsActive = `start`;

        this.thumbnailsWrap = ``;

        this.thumbnailsVisibleDefault = 4;

        this.currentBreakpoint = null;

        /**
         * Включаем или отключаем возможность свайпа
         * @type {boolean}
         */
        this.noSwipe = null;

        /**
         * Включаем или отключаем автоматическое пролистывание слайдера
         * @type {boolean}
         */
        this.autoplay = null;
        this.autoplaySpeed = null;
        this.stopOnClick = null;

        /**
         * Включаем или отключаем зацикливание слайдера
         * @type {boolean}
         */
        this.infinityLoop = null;
    }
    /* eslint-disable */
    init(options) {
        this.sliderLength = options.modify === 'purchase_slide' ? 3 : 1;
        this.slider = options.slider;
        this.arrowPosition = options.arrowPosition || `slide`; // slide or allSlider
        this.counterPosition = options.counterPosition || `caption`; // slide or allSlider
        this.dotsPosition = options.dotsPosition || `caption`;
        this.thumbnails = options.thumbnails || false;
        this.caption = options.caption || false;
        this.slidesWrap = options.slidesWrap || `.j-slides`;
        this.captionWrap = options.captionWrap || `.j-caption`;
        this.slideVisible = options.slideVisible || this.slideVisibleDefaul;
        this.slidesItems = Array.prototype.slice.call(this.slider.querySelector(this.slidesWrap).children);
        this.autoplay = options.autoplay && this.slidesItems.length > this.sliderLength || false;
        this.arrow = options.arrow && this.slidesItems.length > this.sliderLength || false;
        this.counter = options.counter && this.slidesItems.length > this.sliderLength || false;
        this.duration = options.duration || `0.3s`;
        this.modify = options.modify;
        this.destroy = options.destroy || false;
        this.responsive = options.responsive || false;
        this.noSwipe = options.noSwipe || false;
        this.autoplay = options.autoplay && this.slidesItems.length > this.sliderLength || false;
        this.autoplaySpeed = options.autoplaySpeed || 3000;
        this.stopOnClick = options.stopOnClick || false;
        this.infinityLoop = this.responsive ? false : options.infinityLoop || false;
        // j-класс указан в шаблоне для точек. Он не предназначен для смены через настройки слайдера
        this.dotsWrapClass = `.j-slider-dots`;
        this.counterCurrentSlideClass = `.j-counter-current`;

        if (this.thumbnails) {
            this.thumbnailsActive = options.thumbnailsActive || `start`;
            this.thumbnailsVisible = options.thumbnailsVisible || this.thumbnailsVisibleDefault;
            this.thumbnailsWrap = options.thumbnailsWrap || `.j-thumbnails`;
        }

        this._subscribes();
        this._getBreakpoints();
        this._setCountSlides();
        this._createSlider();
        this._addSlider();
        this._getSliderParamters();
        this._bindEvents();
        this._disableArrow();
        this._initInfinityLoop();
        this._setActiveClass();
        this._ready();

        if (this.autoplay) {
            this._slideAutoplay(true);
        }

        this._openFullSize();
    }

    /* eslint-enable */

    /**
     * Создаем элементы галереи
     * @private
     */
    _createSlider() {
        this.slider.style.opacity = `0`;

        this.createSlides();

        if (this.thumbnails) {
            this._createThumbnails();
        }

        if (this.caption) {
            this._createCaption();
        }

        if (this.dots) {
            this._createDots();
        }

        if (this.arrow) {
            this._createArrows();
        }

        if (this.counter) {
            this._createCounter();
        }
    }

    /**
     * Добавляем элементы галереи на страницу
     * @private
     */
    _addSlider() {
        Utils.clearHtml(this.slider);
        Utils.insetContent(this.slider, this.slidesHtml);

        if (this.thumbnails) {
            Utils.insetContent(this.slider, this.thumbnailsHtml);
        }

        if (this.caption) {
            Utils.insetContent(this.slider.lastElementChild, this.captionHtml);
        }

        if (this.dots) {
            this._addDots();
        }

        if (this.arrow) {
            this._addArrows();
        }

        if (this.counter) {
            this._addCounter();
        }
    }

    /**
     * Добавляем стрелки в дом
     * @private
     */
    _addArrows() {
        switch (this.arrowPosition) {
            case `slide`:
                Utils.insetContent(this.slider.firstElementChild, this.arrowHtml);
                break;
            case `allSlider`:
                Utils.insetContent(this.slider, this.arrowHtml);
                break;
            case `caption`:
                Utils.insetContent(this.slider.lastElementChild, this.arrowHtml);
                break;
            default:
                Utils.insetContent(this.slider.firstElementChild, this.arrowHtml);
        }

        this.arrowLeft = this.slider.querySelector('.j-arrow-left');
        this.arrowRight = this.slider.querySelector('.j-arrow-right');
    }

    _addDots() {
        switch (this.dotsPosition) {
            case `caption`:
                Utils.insetContent(this.slider.lastElementChild, this.dotsHtml);
                break;
            default:
                Utils.insetContent(this.slider, this.dotsHtml);
        }
    }

    _addCounter() {
        switch (this.counterPosition) {
            case `slide`:
                Utils.insetContent(this.slider.firstElementChild, this.counterHtml);
                break;
            case `caption`:
                Utils.insetContent(this.slider.lastElementChild, this.counterHtml);
                break;
            default:
                Utils.insetContent(this.slider, this.counterHtml);
        }
    }

    /**
     * Создаем основные большие слайды
     */
    createSlides() {
        this.slidesHtml = this._createElement(slideTemplate, this.slidesWrap, this.slideVisible);
    }

    /**
     * Создаем тумбочки для слайдера
     * @private
     */
    _createThumbnails() {
        this.thumbnailsHtml = this._createElement(thumbnailsTemplate, this.thumbnailsWrap, this.thumbnailsVisible);
    }

    /**
     * Создаем подписи к слайдеру
     * @private
     */
    _createCaption() {
        const captionData = this._getCaptionData();

        if (captionData.hasItems) {
            this.captionHtml = captionTemplate(captionData);
        } else {
            this.caption = false;
            this.slider.classList.add('slider__caption_is_empty');
        }
    }

    /**
     * Создаем точки для слайдера
     * @private
     */
    _createDots() {
        this.dotsHtml = dotsTemplate({items: this.slidesItems});
    }

    /**
     * Создаем счётчик слайдов
     * @private
     */
    _createCounter() {
        this.counterHtml = counterTemplate({items: this.slidesItems});
    }

    /**
     * Создаем стрелки слайдера
     * @private
     */
    _createArrows() {
        this.arrowHtml = arrowTemplate();
    }

    /**
     * Создаем элемент галереи - блок со слайдерами или блок с тумбочками
     * @param {object} template - twig шаблон
     * @param {string} wrapClass - класс обертки для тумбочек или слайдов
     * @param {number} items - количество элементов к показу (по умолчанию один слайд и 4 тумбочки)
     * @returns {string} - разметка блока галереи
     * @private
     */
    _createElement(template, wrapClass, items) {
        return template(this._getElements(wrapClass, items));
    }

    /**
     * Получаем элементы галереи - для слайдов или тумбочек
     * @param {string} wrapClass - класс обертки
     * @param {number|string} items - количество элементов к показу (по умолчанию один слайд и 4 тумбочки)
     * для туббочек можно задать auto
     * @returns {object} - объект
     * @private
     */
    _getElements(wrapClass, items) {
        const substringNumber = 1;
        const elementsArray = Array.prototype.slice.call(this.slider.querySelector(wrapClass).children);
        const stringArray = elementsArray.map((element) => {
            return element.outerHTML;
        });

        let widthItem = this._getWidth(items);

        if (typeof items === 'string') {
            widthItem = false;
        }

        return {
            wrap  : wrapClass.substring(substringNumber),
            width : `${widthItem}%`,
            items : stringArray,
            modify: this.modify ? this.modify : false
        };
    }

    /**
     * Вычисляем ширину тумбочки
     * @param {number} items - количество элементов к показу (по умолчанию один слайд и 4 тумбочки)
     * @returns {number} - ширина тумбочки в процентах
     * @private
     */
    _getWidth(items) {
        const percent = 100;

        return percent / items;
    }

    _getCaptionData() {
        const slides = [].slice.call(this.slider.querySelector(this.slidesWrap).children);
        const widthItem = this._getWidth(this.slideVisible);
        const captionText = slides.map((item) => {
            return item.dataset.caption;
        });

        return {
            width   : `${widthItem}%`,
            items   : captionText,
            hasItems: captionText.join('').length > 0
        };
    }

    /**
     * биндим события
     * @privatehttps://gitlab.kelnik.pro/renovation/spbren/graphs/dev
     */
    _bindEvents() {
        this._slideEvents();

        if (this.thumbnails) {
            this._thumbnailsEvents();
        }

        if (this.dots) {
            this._dotsEvents();
        }

        if (this.arrow) {
            this._arrowEvents();
        }

        document.addEventListener('keyup', (event) => {
            const keyCodeLeft = 37;
            const keyCodeRight = 39;

            // eslint-disable-next-line max-len
            if (this.slider && (this.slider.classList.contains('is-full-size') || this.slider.classList.contains('slider_theme_progress'))) {
                if (event.keyCode === keyCodeLeft) {
                    this._onTurnSlide(-1);
                } else if (event.keyCode === keyCodeRight) {
                    this._onTurnSlide(1);
                }
            }
        });

        window.addEventListener('resize', this._resizeWindow.bind(this));
    }

    /**
     * Вешаем события на сам слайд - свайп
     * @private
     */
    _slideEvents() {
        if (this.noSwipe || this.slidesItems.length === 1) {
            return;
        }

        const touchEvent = new Hammer(this.slider);

        touchEvent.on('swiperight', this._onSwipe.bind(this));
        touchEvent.on('swipeleft', this._onSwipe.bind(this));
    }

    /**
     * Вешаем события на тумбочики - клик по тумбочкам и свайп по ним же
     * @private
     */
    _thumbnailsEvents() {
        const thumbnailWrap = this.slider.querySelector(this.thumbnailsWrap);
        const thumbnailsArray = Array.prototype.slice.call(thumbnailWrap.children);
        const touchEvent = new Hammer(thumbnailWrap);

        touchEvent.on('swiperight', this._onSwipe.bind(this));
        touchEvent.on('swipeleft', this._onSwipe.bind(this));

        thumbnailsArray.forEach((thumbnail) => {
            thumbnail.addEventListener('click', this._onThumbnails.bind(this));
        });
    }

    /**
     * Вешаем события на точки
     * @private
     */
    _dotsEvents() {
        const dotsArray = Array.prototype.slice.call(this.slider.querySelector(`.j-slider-dots`).children);

        dotsArray.forEach((dot) => {
            dot.addEventListener('click', this._onDots.bind(this));
        });
    }

    /**
     * Вешаем события на стрелки галереи
     * @private
     */
    _arrowEvents() {
        const arrowsArray = Array.prototype.slice.call(this.slider.querySelector(`.j-slider-arrows`).children);

        arrowsArray.forEach((arrow) => {
            arrow.addEventListener('click', this._onArrow.bind(this));
        });
    }

    /**
     * Отрабатываем тач события свайпа
     * @param {event} event - событие
     * @private
     */
    _onSwipe(event) {
        const left = -1;
        const right = 1;
        const slideDirection = event.type === `swiperight` ? left : right;

        this._onTurnSlide(slideDirection);
    }

    /**
     * Отрабатываем событие клика по стрелке
     * @param {event} event - событие
     * @private
     */
    _onArrow(event) {
        const negativeDirection = -1;
        const arrow = event.target.closest('.j-arrow');
        const slideDirection = Utils.getElementIndex(arrow) ? Utils.getElementIndex(arrow) : negativeDirection;

        this._onTurnSlide(slideDirection);
    }

    /**
     * Переключние слайдера
     * @param {number} direction - в каком направлении переключать (1-next, -1-prev)
     */
    _onTurnSlide(direction) {
        this._getSlidesParam();

        if (this._checkTransform(direction)) {
            this._changeActive(direction);
            this._slideTo(this.duration);

            if (this.thumbnails) {
                this._thumbnailsTo(this.duration);
            }

            if (this.caption) {
                this._captionTo(this.duration);
            }
        } else if (this.infinityLoop) {
            this._changeActive(direction);
            this._slideTo(this.duration);

            if (this.caption) {
                this._captionTo(this.duration);
            }

            this._rewindSlider(direction);
        }

        this._disableArrow();

        if (this.slider.classList.contains('is-full-size')) {
            const slides = [].slice.call(this.slider.querySelector(this.slidesWrap).children);
            const slide = slides[this.activeSlide];
            const picture = slide.querySelector('picture');

            if (picture) {
                const sources = Array.from(picture.querySelectorAll('source'));

                sources.forEach((source) => {
                    if (source.dataset.big) {
                        source.setAttribute('srcset', source.dataset.big);
                    }
                });
            }
        }
    }

    /**
     * Отрабатываем событие клика по тумбочке
     * @param {event} event - событие
     * @private
     */
    _onThumbnails(event) {
        const thumbnail = event.currentTarget;
        const thumbnailIndex = Utils.getElementIndex(thumbnail);

        this._setActive(thumbnailIndex);
        this._slideTo(this.duration);
        this._thumbnailsTo(this.duration);
    }

    /**
     * Отрабатываем событие клика по точке
     * @param {event} event - событие
     * @private
     */
    _onDots(event) {
        const dot = event.currentTarget;
        const dotIndex = Utils.getElementIndex(dot);

        this._setActive(dotIndex);
        this._slideTo(this.duration);

        if (this.thumbnails) {
            this._thumbnailsTo(this.duration);
        }

        if (this.caption) {
            this._captionTo(this.duration);
        }
    }

    /**
     * Дисаблим первую или послеюнюю стрелку
     * @private
     */
    _disableArrow() {
        if (!this.arrowLeft || !this.arrowRight || this.infinityLoop) {
            return;
        }

        const firstSlideIndex = 0;
        const lastSlideIndex = this.modify === 'purchase_slide' ?
            this.slidesItems.length - 3 :
            this.slidesItems.length - 1;


        if (this.activeSlide === firstSlideIndex) {
            this.arrowLeft.classList.add(this.isDisable);
            this.arrowRight.classList.remove(this.isDisable);
        } else if (this.activeSlide === lastSlideIndex) {
            this.arrowRight.classList.add(this.isDisable);
            this.arrowLeft.classList.remove(this.isDisable);
        } else {
            this.arrowLeft.classList.remove(this.isDisable);
            this.arrowRight.classList.remove(this.isDisable);
        }

        if (this.slidesItems.length === 1) {
            this.arrowRight.classList.add(this.isDisable);
            this.arrowLeft.classList.add(this.isDisable);
        }
    }


    /**
     * Проверка на возможность скроллить слайды
     * @param {number} direction - единица (следующий слайд) или минус единица (предыдущий слайд)
     * @returns {boolean} - true: можем скроллить галерею, false: уперлись в крайние значения первого или последнего слайда
     * @private
     */
    _checkTransform(direction) {
        const zeroIndex = 0;
        const offset = this.infinityLoop ? this.slideVisible : 0;
        const nextSlide = this.activeSlide + direction - offset;

        return nextSlide >= zeroIndex && nextSlide !== this.slidesItems.length;
    }

    /**
     * меняем номер активного слайда в большую или мньшую сторону на единицу
     * @param {number} direction - единица или минус единица.
     * @private
     */
    _changeActive(direction) {
        this.activeSlide = this.activeSlide + direction;

        this._setActiveClass();

        if (this.counter) {
            this._setCurrentSlideNumber();
        }
    }

    /**
     * меняем номер активного слайда на нужный
     * @param {number} index - индекс активного слайда
     * @private
     */
    _setActive(index) {
        this.activeSlide = index;

        this._setActiveClass();
    }

    _setCurrentSlideNumber() {
        const currentSlide = this.slider.querySelector(this.counterCurrentSlideClass);
        const count = this.infinityLoop ? this.activeSlide + 1 - this.slideVisible : this.activeSlide + 1;

        if (count === 0) {
            currentSlide.innerHTML = this.slidesItems.length;
        } else if (count === this.slidesItems.length + 1) {
            currentSlide.innerHTML = 1;
        } else {
            currentSlide.innerHTML = count;
        }
    }

    /**
     * Добавляем классы активным элементам
     * @private
     */
    _setActiveClass() {
        const slides = [].slice.call(this.slider.querySelector(this.slidesWrap).children);

        if (!slides.length) {
            return;
        }

        slides.forEach((slide) => {
            slide.classList.remove(this.isActive);
        });

        slides[this.activeSlide].classList.add(this.isActive);

        if (this.dots) {
            const dots = [].slice.call(this.slider.querySelector(this.dotsWrapClass).children);

            dots.forEach((dot) => {
                dot.classList.remove(this.isActive);
            });

            dots[this.activeSlide].classList.add(this.isActive);
        }

        if (this.thumbnails) {
            const thumbnails = [].slice.call(this.slider.querySelector(this.thumbnailsWrap).children);

            thumbnails.forEach((item) => {
                item.classList.remove(this.isActive);
            });

            thumbnails[this.activeSlide].classList.add(this.isActive);
        }
    }

    /**
     * Вычисляем позицию для смещения слайдера
     * @returns {number} - число
     * @private
     */
    _getSlidePosition() {
        return this.slidesData.width * this.activeSlide;
    }

    /**
     * Вычисляем позицию для смещения подписи
     * @returns {number} - число
     * @private
     */
    _getCaptionPosition() {
        return this.captionData.width * this.activeSlide;
    }

    /**
     * Вычисляем позицию для смещения тумбочек
     * @param {number} activeThumbnails - индекс тубочки
     * @returns {number} - значение для трансформа тубмобчек
     * @private
     */
    _getThumbnailsPosition(activeThumbnails) {
        const thumbWidthVisible = this.slider.querySelector(this.thumbnailsWrap).offsetWidth;
        // индекс активного слайда с которого начинается прокрутка тумбочек
        // ннада для того, что бы при активном 0 и 1 индексах тумбочки не прокручивались и у юзера всегда была видна
        // одна тумбочка слева от активной
        const startTranslate = 2;
        // ннада для высчитывания корректного размера сдвига тумбочек. цель - видимость одно тумбочки слева от активной
        const thumbnailsModify = 1;

        // let translate = this.thumbData.width * (activeThumbnails - thumbnailsModify);
        //
        // if ()
        const translate = this.thumbData.width * (activeThumbnails - thumbnailsModify);

        if (activeThumbnails < startTranslate || thumbWidthVisible > this.thumbData.fullWidth) {
            return this.thumbData.minTranslate;
        }

        if (translate < this.thumbData.maxTranslate) {
            return translate;
        }

        return this.thumbData.maxTranslate;
    }

    /**
     * Перелистывает к переданной позиции слайды.
     * @param {string} duration - время анимации
     * @private
     */
    _slideTo(duration) {
        const position = this._getSlidePosition();
        const slidesWrap = this.slider.querySelector(this.slidesWrap);

        slidesWrap.style.transform = `translate3d(-${position}px, 0, 0)`;
        slidesWrap.style.transitionDuration = duration;
    }

    /**
     * Перелистывает к переданной позиции подпись.
     * @param {string} duration - время анимации
     * @private
     */
    _captionTo(duration) {
        const position = this._getCaptionPosition();
        const captionWrap = this.slider.querySelector(this.captionWrap);

        captionWrap.style.transform = `translate3d(-${position}px, 0, 0)`;
        captionWrap.style.transitionDuration = duration;
    }

    /**
     * Перелистывает к переданной позиции тумбочки.
     * @param {String} duration - время анимации
     * @private
     */
    _thumbnailsTo(duration) {
        const position = this._getThumbnailsPosition(this.activeSlide);
        const thumbnailsWrap = this.slider.querySelector(this.thumbnailsWrap);

        thumbnailsWrap.style.transform = `translate3d(-${position}px, 0, 0)`;
        thumbnailsWrap.style.transitionDuration = duration;
    }

    /**
     * Получаем размеры слайдов и тумбочек.
     * @private
     */
    _getSliderParamters() {
        this._getSlidesParam();

        if (this.thumbnails) {
            this._getThumbParams();
        }

        if (this.caption) {
            this._getCaptionParams();
        }
    }

    /**
     * Получаем размеры слайдов и ограничения для прокрутки
     * @private
     */
    _getSlidesParam() {
        const count = this.slideVisible;
        const slidesArray = [].slice.call(this.slider.querySelector(this.slidesWrap).children);

        if (!slidesArray.length) {
            return;
        }

        const offsetWidth = Number(slidesArray[0].getBoundingClientRect().width.toFixed(4));

        this.slidesData = {
            width       : offsetWidth,
            fullWidth   : offsetWidth * slidesArray.length,
            minTranslate: 0,
            maxTranslate: (offsetWidth * slidesArray.length) - (offsetWidth * count)
        };
    }

    /**
     * Получаем размеры подписей и ограничения для прокрутки
     * @private
     */
    _getCaptionParams() {
        const zero = 0;
        const captionArr = [].slice.call(this.slider.querySelector(this.captionWrap).children);
        const count = this.slideVisible;

        if (!captionArr.length) {
            return;
        }

        this.captionData = {
            width       : captionArr[zero].offsetWidth,
            fullWidth   : captionArr[zero].offsetWidth * captionArr.length,
            minTranslate: 0,
            maxTranslate: (captionArr[zero].offsetWidth * captionArr.length) - (captionArr[zero].offsetWidth * count)
        };
    }

    /**
     * Получаем размеры тумбочек и ограничения для прокрутки
     * @private
     */
    _getThumbParams() {
        const zero = 0;
        const thumbArray = Array.prototype.slice.call(this.slider.querySelector(this.thumbnailsWrap).children);
        const thumbWidthVisible = this.slider.querySelector(this.thumbnailsWrap).offsetWidth;

        this.thumbData = {
            width       : thumbArray[zero].offsetWidth,
            fullWidth   : thumbArray[zero].offsetWidth * thumbArray.length,
            minTranslate: 0,
            maxTranslate: (thumbArray[zero].offsetWidth * thumbArray.length) - thumbWidthVisible
        };
    }

    /**
     * Показывает слайдер когда все методы выполнятся.
     * @private
     */
    _ready() {
        this._setIntegerWidth();
        this.slider.style.opacity = `1`;

        observer.publish('sliderReady', this);
    }

    /**
     * Устанавливает целочисленную ширину для галлереи, чтобы избежать полос по бокам слайдера
     * @private
     */
    _setIntegerWidth() {
        if (this.responsive) {
            return;
        }

        this.slider.style.width = `${this.slidesData.width * this.slideVisible}px`;
    }

    /**
     * Удаляет фиксированную ширину галлереи
     * @private
     */
    _removeIntegerWidth() {
        this.slider.style.width = ``;
    }

    /**
     * Пересчитываем значения размеров элементов слайдера и смещаем слайды без анимации
     * @private
     */
    _resizeWindow() {
        if (this.responsive) {
            this._setCountSlides();
        }

        this._removeIntegerWidth();
        this._getSliderParamters();
        this._setIntegerWidth();
        this._slideTo(`0s`);

        if (this.thumbnails) {
            this._thumbnailsTo(`0s`);
        }

        if (this.caption) {
            this._captionTo(`0s`);
        }
    }

    _redrawSlides() {
        const slidesArray = Array.prototype.slice.call(this.slider.querySelector(this.slidesWrap).children);
        const width = this._getElements(this.slidesWrap, this.slideVisible).width;

        slidesArray.forEach((slide) => {
            slide.style['min-width'] = width;
            slide.style['flex-basis'] = width;
        });
    }

    /**
     * Задаем количество показаных слайдов в случаем адаптивного слайдера
     * @private
     */
    _setCountSlides() {
        if (!this.responsive) {
            return;
        }

        if (this._getCurrentBreakpoint() !== this.currentBreakpoint) {
            this.currentBreakpoint = this._getCurrentBreakpoint();
            const key = `${this.currentBreakpoint}`;

            this.slideVisible = this.responsive[key];

            this._redrawSlides();
        }
    }

    _subscribes() {
        observer.subscribe('tabs:change', () => {
            this._resizeWindow();
        });
    }

    /**
     * Задаем массив брекпоинтов из настроек слайдера
     * @private
     */
    _getBreakpoints() {
        if (!this.responsive) {
            return;
        }

        const breakpointArray = [];

        for (const key in this.responsive) {
            if (this.responsive.hasOwnProperty(key)) {
                breakpointArray.push(parseInt(key, 10));
            }
        }

        this.breakpointArray = breakpointArray;
    }

    /**
     * Возвращаем брейкпоинт из указанных в настройках слайдера, соответствующий текущей ширине украна
     * @return {number} breakpoint - текущий брейкпоинт
     * @private
     */
    _getCurrentBreakpoint() {
        let breakpoint = null;

        this.breakpointArray.forEach((point) => {
            if (window.matchMedia(`(min-width: ${point}px)`).matches) {
                breakpoint = point;
            }
        });

        return breakpoint;
    }

    /**
     * Запускаем автопроигрывание слайдов
     * @private
     */
    _slideAutoplay() {
        const slideDirection = 1;
        let auto = null;

        const startAutoplay = () => {
            auto = setInterval(() => {
                if (this._checkTransform(slideDirection)) {
                    this._changeActive(slideDirection);
                    this._slideTo(this.duration);

                    if (this.caption) {
                        this._captionTo(this.duration);
                    }

                    if (this.thumbnails) {
                        this._thumbnailsTo(this.duration);
                    }
                } else if (this.infinityLoop) {
                    this._changeActive(slideDirection);
                    this._slideTo(this.duration);

                    if (this.caption) {
                        this._captionTo(this.duration);
                    }

                    this._rewindSlider(slideDirection);
                }
            }, this.autoplaySpeed);
        };

        startAutoplay();

        if (this.stopOnClick) {
            this.slider.addEventListener('touchstart', () => {
                clearInterval(auto);
                startAutoplay();
            });
        }
    }

    _initInfinityLoop() {
        if (!this.infinityLoop) {
            return;
        }

        this._cloneSlides();

        if (this.caption) {
            this._cloneCaptions();
        }

        if (this.dots) {
            this._cloneDots();
        }

        this._setActive(this.slideVisible);
        this._slideTo(`0s`);

        if (this.caption) {
            this._captionTo(`0s`);
        }
    }

    _rewindSlider() {
        if (!this.infinityLoop) {
            return;
        }

        const animationDelay = 300;
        const newActiveSlide = this.activeSlide === 0 ?
            this.slidesItems.length - 1 + this.slideVisible :
            this.slideVisible;

        if (this.arrow) {
            this._preventArrowsClick();
        }

        setTimeout(() => {
            this.activeSlide = newActiveSlide;
            this._setActive(newActiveSlide);
            this._slideTo(`0s`);

            if (this.caption) {
                this._captionTo(`0s`);
            }

            if (this.arrow) {
                this._allowArrowsClick();
            }
        }, animationDelay);
    }

    _cloneSlides() {
        if (!this.infinityLoop) {
            return;
        }

        const slidesArray = Array.prototype.slice.call(this.slider.querySelector(this.slidesWrap).children);

        for (let i = 0; i < this.slideVisible; i++) {
            const firstSlides = slidesArray[i];
            const lastSlides = slidesArray[slidesArray.length - 1 - i];
            const beforeElem = firstSlides.cloneNode(true);
            const afterElem = lastSlides.cloneNode(true);
            const container = this.slider.querySelector(this.slidesWrap);

            afterElem.classList.add('cloned-slide');
            beforeElem.classList.add('cloned-slide');

            container.appendChild(beforeElem);
            container.insertBefore(afterElem, container.children[0]);
        }
    }

    _cloneCaptions() {
        if (!this.infinityLoop) {
            return;
        }

        const captionsArray = Array.prototype.slice.call(this.slider.querySelector(this.captionWrap).children);

        for (let i = 0; i < this.slideVisible; i++) {
            const firstCaptions = captionsArray[i];
            const lastCaptions = captionsArray[captionsArray.length - 1 - i];
            const beforeElem = firstCaptions.cloneNode(true);
            const afterElem = lastCaptions.cloneNode(true);
            const container = this.slider.querySelector(this.captionWrap);

            afterElem.classList.add('cloned-slide');
            beforeElem.classList.add('cloned-slide');

            container.appendChild(beforeElem);
            container.insertBefore(afterElem, container.children[0]);
        }
    }

    _cloneDots() {
        if (!this.infinityLoop) {
            return;
        }

        const dotsArray = Array.prototype.slice.call(this.slider.querySelector(this.dotsWrapClass).children);

        for (let i = 0; i < this.slideVisible; i++) {
            const firstCaptions = dotsArray[i];
            const lastCaptions = dotsArray[dotsArray.length - 1 - i];
            const beforeElem = firstCaptions.cloneNode(true);
            const afterElem = lastCaptions.cloneNode(true);
            const container = this.slider.querySelector(this.dotsWrapClass);

            afterElem.classList.add('cloned-slide');
            beforeElem.classList.add('cloned-slide');

            container.appendChild(beforeElem);
            container.insertBefore(afterElem, container.children[0]);
        }
    }

    _preventArrowsClick() {
        this.arrowLeft.style['pointer-events'] = 'none';
        this.arrowRight.style['pointer-events'] = 'none';
        this.arrowLeft.style['cursor'] = 'pointer';
        this.arrowRight.style['cursor'] = 'pointer';
    }

    _allowArrowsClick() {
        this.arrowLeft.style['pointer-events'] = null;
        this.arrowRight.style['pointer-events'] = null;
        this.arrowLeft.style['cursor'] = null;
        this.arrowRight.style['cursor'] = null;
    }

    // eslint-disable-next-line max-lines-per-function
    _openFullSize() {
        const galleryPopupBtn = this.slider.previousElementSibling;
        const header = document.querySelector('.j-header');

        if (galleryPopupBtn) {
            const galleryPopupBlock = galleryPopupBtn.closest('.j-gallery-popup-block');
            const slider = this.slider;

            // eslint-disable-next-line func-names
            galleryPopupBtn.addEventListener('click', () => {
                const heightBlock = galleryPopupBlock.clientHeight;
                const allPictures = Array.from(slider.querySelectorAll('picture'));

                if (allPictures && allPictures.length) {
                    allPictures.forEach((picture) => {
                        if (galleryPopupBtn.classList.contains('is-show')) {
                            const sources = Array.from(picture.querySelectorAll('source'));

                            sources.forEach((source) => {
                                source.setAttribute('srcset', source.dataset.origin);
                            });
                        } else if (picture.closest('.slider__item.is-active')) {
                            const sources = Array.from(picture.querySelectorAll('source'));

                            sources.forEach((source) => {
                                source.setAttribute('srcset', source.dataset.big);
                            });
                        }
                    });
                }

                const allImg = Array.from(slider.querySelectorAll('img'));

                // eslint-disable-next-line no-negated-condition,no-invalid-this
                if (!galleryPopupBtn.classList.contains('is-show')) {
                    if (header) {
                        Utils.hide(header);
                    }

                    // eslint-disable-next-line no-invalid-this
                    galleryPopupBtn.classList.add('is-show');
                    galleryPopupBlock.style.height = `${heightBlock}px`;
                    slider.classList.add('is-full-size');

                    if (window.innerWidth >= 1280) {
                        allImg.map((img) => {
                            const fullImg = img.dataset.big;

                            return img.setAttribute('src', fullImg);
                        });
                    }

                    Utils.bodyFixed(slider);
                } else {
                    if (header) {
                        Utils.show(header);
                    }

                    // eslint-disable-next-line no-invalid-this
                    galleryPopupBtn.classList.remove('is-show');
                    galleryPopupBlock.style.height = 'auto';
                    slider.classList.remove('is-full-size');

                    if (window.innerWidth >= 1280) {
                        allImg.map((img) => {
                            const fullImg = img.dataset.origin;

                            return img.setAttribute('src', fullImg);
                        });
                    }

                    Utils.bodyStatic();
                }
            });

            galleryPopupBtn.addEventListener('click', this._resizeWindow.bind(this));
        }
        setTimeout(() => {
            observer.publish('lazyLoad:refresh');
        }, 0);
    }
}

export default Slider;
